import React, { useEffect, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { UserCircle, Shield, Eye, EyeOff, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ViewType = 'login' | 'register' | 'forgot';
export function LoginPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [view, setView] = useState<ViewType>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Reset states when changing views
  const handleViewChange = (newView: ViewType) => {
    setView(newView);
    setSuccessMessage('');
    setPassword('');
    setConfirmPassword('');
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccessMessage('');
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      if (view === 'forgot') {
        setSuccessMessage('Інструкції надіслано на вашу пошту');
        setEmail('');
      } else if (view === 'register') {
        setSuccessMessage('Реєстрація успішна! Тепер ви можете увійти.');
        setView('login');
        setPassword('');
        setConfirmPassword('');
      } else {
        setSuccessMessage('Успішний вхід!');
        // In a real app, redirect here
      }
    }, 1500);
  };
  const handleGoogleLogin = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setSuccessMessage('Успішний вхід через Google!');
    }, 1000);
  };
  const variants = {
    enter: {
      opacity: 0,
      x: 20
    },
    center: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -20
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-795">
      <Header data-id="element-796" />
      <main className="flex-grow pt-24 pb-20 flex flex-col justify-center" data-id="element-797">
        <div className="max-w-md w-full mx-auto px-4 sm:px-6 lg:px-8" data-id="element-798">
          <div className="text-center mb-8" data-id="element-799">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4" data-id="element-800">
              <UserCircle className="h-8 w-8 text-primary" data-id="element-801" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-primary" data-id="element-802">
              {view === 'login' && 'Вхід до кабінету'}
              {view === 'register' && 'Реєстрація'}
              {view === 'forgot' && 'Відновлення пароля'}
            </h1>
            {view === 'forgot' && <p className="mt-3 text-sm text-gray-600" data-id="element-803">
                Введіть вашу електронну пошту і ми надішлемо інструкції для
                відновлення пароля.
              </p>}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 sm:p-8 overflow-hidden relative" data-id="element-804">
            {successMessage && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-6 p-3 bg-green-50 text-green-700 text-sm rounded-md border border-green-200 text-center" data-id="element-805">
                {successMessage}
              </motion.div>}

            <AnimatePresence mode="wait" data-id="element-806">
              <motion.div key={view} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.2
            }} data-id="element-807">
                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-808">
                  {/* Registration Fields */}
                  {view === 'register' && <div className="grid grid-cols-2 gap-4" data-id="element-809">
                      <div data-id="element-810">
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-811">
                          Прізвище
                        </label>
                        <input id="lastName" type="text" required value={lastName} onChange={e => setLastName(e.target.value)} className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" data-id="element-812" />
                      </div>
                      <div data-id="element-813">
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-814">
                          Ім'я
                        </label>
                        <input id="firstName" type="text" required value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" data-id="element-815" />
                      </div>
                    </div>}

                  {/* Email Field (All Views) */}
                  <div data-id="element-816">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-817">
                      Електронна пошта
                    </label>
                    <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" data-id="element-818" />
                  </div>

                  {/* Password Field (Login & Register) */}
                  {(view === 'login' || view === 'register') && <div data-id="element-819">
                      <div className="flex justify-between items-center mb-1" data-id="element-820">
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-821">
                          Пароль
                        </label>
                        {view === 'login' && <button type="button" onClick={() => handleViewChange('forgot')} className="text-xs text-primary/70 hover:text-primary hover:underline transition-colors" data-id="element-822">
                            Забули пароль?
                          </button>}
                      </div>
                      <div className="relative" data-id="element-823">
                        <input id="password" type={showPassword ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" data-id="element-824" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" data-id="element-825">
                          {showPassword ? <EyeOff className="h-4 w-4" data-id="element-826" /> : <Eye className="h-4 w-4" data-id="element-827" />}
                        </button>
                      </div>
                    </div>}

                  {/* Confirm Password Field (Register Only) */}
                  {view === 'register' && <div data-id="element-828">
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-829">
                        Підтвердження пароля
                      </label>
                      <div className="relative" data-id="element-830">
                        <input id="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" data-id="element-831" />
                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" data-id="element-832">
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" data-id="element-833" /> : <Eye className="h-4 w-4" data-id="element-834" />}
                        </button>
                      </div>
                    </div>}

                  {/* Submit Button */}
                  <button type="submit" disabled={isLoading} className="w-full bg-primary text-white rounded-md py-2.5 font-semibold text-sm hover:bg-primary-light transition-colors disabled:opacity-70 flex items-center justify-center mt-6" data-id="element-835">
                    {isLoading ? <Loader2 className="h-5 w-5 animate-spin" data-id="element-836" /> : view === 'login' ? 'Увійти' : view === 'register' ? 'Зареєструватися' : 'Надіслати інструкції'}
                  </button>
                </form>

                {/* Google Login (Login Only) */}
                {view === 'login' && <>
                    <div className="relative my-6" data-id="element-837">
                      <div className="absolute inset-0 flex items-center" data-id="element-838">
                        <div className="w-full border-t border-gray-200" data-id="element-839"></div>
                      </div>
                      <div className="relative flex justify-center text-sm" data-id="element-840">
                        <span className="px-2 bg-white text-gray-500" data-id="element-841">або</span>
                      </div>
                    </div>

                    <button type="button" onClick={handleGoogleLogin} disabled={isLoading} className="w-full flex items-center justify-center gap-3 py-2.5 px-4 bg-white border border-gray-300 rounded-md font-medium text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-70" data-id="element-842">
                      <svg className="h-5 w-5" viewBox="0 0 24 24" data-id="element-843">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" data-id="element-844" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-845" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-846" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-847" />
                      </svg>
                      Увійти через Google
                    </button>
                  </>}

                {/* View Toggles */}
                <div className="mt-6 text-center text-sm" data-id="element-848">
                  {view === 'login' && <p className="text-gray-600" data-id="element-849">
                      Немає акаунту?{' '}
                      <button onClick={() => handleViewChange('register')} className="text-primary font-medium hover:underline transition-colors" data-id="element-850">
                        Зареєструватися
                      </button>
                    </p>}
                  {view === 'register' && <p className="text-gray-600" data-id="element-851">
                      Вже маєте акаунт?{' '}
                      <button onClick={() => handleViewChange('login')} className="text-primary font-medium hover:underline transition-colors" data-id="element-852">
                        Увійти
                      </button>
                    </p>}
                  {view === 'forgot' && <button onClick={() => handleViewChange('login')} className="text-primary font-medium hover:underline transition-colors" data-id="element-853">
                      Повернутися до входу
                    </button>}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Security Note */}
            <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-854">
              <div className="flex items-start gap-3 text-gray-500" data-id="element-855">
                <Shield className="h-4 w-4 mt-0.5 flex-shrink-0 text-primary/50" data-id="element-856" />
                <p className="text-xs leading-relaxed" data-id="element-857">
                  Ваші дані захищені та не передаються третім особам.
                  Авторизація відбувається через захищене з'єднання.
                </p>
              </div>
            </div>
          </div>

          {/* Legal Links */}
          <p className="mt-6 text-center text-xs text-gray-400" data-id="element-858">
            Продовжуючи, ви погоджуєтесь з{' '}
            <a href="#" className="text-primary/70 hover:underline" data-id="element-859">
              умовами використання
            </a>{' '}
            та{' '}
            <a href="#" className="text-primary/70 hover:underline" data-id="element-860">
              політикою конфіденційності
            </a>
            .
          </p>
        </div>
      </main>
      <Footer data-id="element-861" />
    </div>;
}